import { LoadingOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import React, { useEffect, useState } from "react";
import { getPresignedImage } from "../../../../../utils/media";
import { SectionLoader } from "../../../../atoms/SectionLoader/SectionLoader";
import { convertToTitleCase, formatDate } from "../../../../libs/utils";
import { DataDisplayRow } from "../../../../organism";
import { useGetServiceRequest } from "../api";

type AttachmentItem = Record<
  string,
  {
    documentId: string;
    type: string;
    file: string;
    label: string;
  }
>;

type ServiceRequestDetailProps = {
  ticketId?: string;
};

export const ServiceRequestDetail = (props: ServiceRequestDetailProps) => {
  const { ticketId } = props;

  const [documentUrls, setDocumentUrls] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const { serviceRequest, isLoadingServiceRequest } = useGetServiceRequest({ ticketId });

  useEffect(() => {
    (async () => {
      const attachments = serviceRequest?.serviceRequestDetails?.attachment as AttachmentItem[] | undefined;
      if (!attachments?.length) return;

      setIsLoading(true);
      setIsError(false);

      try {
        const allDocs = attachments.flatMap((a) => Object.values(a));
        const documentIds = allDocs.map((d) => d.documentId);

        const presignedDocs = await getPresignedImage(documentIds, true, true);

        const urlMap: Record<string, string> = {};
        presignedDocs.forEach((doc, index) => {
          const key = Object.keys(attachments[index] || {})[0]; // e.g. "passport", "emiratesId"
          if (doc?.location) {
            urlMap[key] = doc.location;
          }
        });

        setDocumentUrls(urlMap);
      } catch (err) {
        console.error("Error fetching document URLs:", err);
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [serviceRequest]);

  if (isLoadingServiceRequest) return <SectionLoader isLoading={isLoadingServiceRequest} containerStyle={{ height: "3rem" }} />;

  const renderLinkOrFallback = (url?: string) => {
    if (isLoading) return <Spin indicator={<LoadingOutlined spin />} size="small" />;
    if (isError || !url) return "-";
    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        View ↗
      </a>
    );
  };

  const details = (serviceRequest?.serviceRequestDetails || {}) as Record<string, React.ReactNode>;
  const attachments = Array.isArray(details.attachment) ? details.attachment : [];

  return (
    <div>
      <DataDisplayRow label="Sr no." value={serviceRequest?.srNumber} />
      <DataDisplayRow label="Category" value={`${serviceRequest?.category?.name} (${serviceRequest?.serviceType})`} />
      <DataDisplayRow label="Status" value={serviceRequest?.ticketStatus?.label} />
      <DataDisplayRow label="Assigned User" value={serviceRequest?.landlord?.email} />
      <DataDisplayRow label="Created Date" value={formatDate(serviceRequest?.createdAt)} />

      {Object.entries(details)
        .filter(([key]) => key !== "attachment")
        .map(([key, value]) => (
          <DataDisplayRow key={key} label={convertToTitleCase(key)} value={value} />
        ))}

      {attachments.map((docObj, idx) => {
        const key = Object.keys(docObj)[0];
        const url = documentUrls?.[key];

        return (
          <DataDisplayRow key={`${key}-${idx}`} label={convertToTitleCase(key)} value={renderLinkOrFallback(url)} />
        );
      })}
    </div>
  );
};
